import { create } from "zustand"
import { devtools, persist } from "zustand/middleware"
import { APP_ID } from "../utils/constants"

/**
 * Persisted store for network-dependent feature flags.
 *
 * Any module that needs to fetch setup/config data from the API and
 * derive a boolean flag can store it here so the sidebar (and other
 * consumers) always has a synchronous, cached value on page reload.
 *
 * Usage:
 *   const { setFlag, getFlag } = useFeatureFlagStore()
 *   setFlag("isPerformanceSetupComplete", true)
 *   const ready = getFlag("isPerformanceSetupComplete")
 */

interface FeatureFlagState {
  flags: Record<string, boolean>
  setFlag: (key: string, value: boolean) => void
  getFlag: (key: string) => boolean | undefined
  resetFlags: () => void
}

export const useFeatureFlagStore = create<FeatureFlagState>()(
  devtools(
    persist(
      (set, get) => ({
        flags: {},
        setFlag: (key, value) =>
          set(
            (state) => ({ flags: { ...state.flags, [key]: value } }),
            false,
            "setFlag"
          ),
        getFlag: (key) => get().flags[key],
        resetFlags: () => set({ flags: {} }, false, "resetFlags"),
      }),
      {
        name: `${APP_ID}.feature-flags`,
      }
    ),
    { name: "FeatureFlagStore" }
  )
)
